import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { ArrowLeft, Target, Zap, ThumbsUp, ThumbsDown, Share2, Home } from "lucide-react";
import { GROUP_ICON, TIMER_ICON, HEART_ICON } from "../../constants/icons";
import { GameTile } from "./CurateGameTile";
import ExplanationPills from "../common/ExplanationPills";
import { useBulkOnlineAvailability } from "../../hooks/useBulkOnlineAvailability";
import { ENERGY_OPTIONS, FAMILIARITY_OPTIONS, getFeelDisplayLabel } from "../../hooks/useSimplifyPreferences";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import { analyticsService } from "../../services/analytics";
import logger from '../../utils/logger';
import { isFirefox } from "../../utils/browserDetection";
import { getGameOwnershipStatus } from "../../utils/gameOwnership";
import ShareWarningBanner from "../common/ShareWarningBanner";
import './ModalComponents.css';
import '../../styles/quickdecision.css';
import '../../styles/share-warning.css';

/**
 * SimplifyResultsPanel - Renders the game recommendations results
 *
 * Props:
 * - recommendations: Array - Ranked game recommendations
 * - disqualifiedGames: Array - Games that didn't match criteria
 * - preferences: Object - User preferences {players, time, energy, familiarity}
 * - toCamelCase: Function - Helper to format text
 * - TIME_OPTIONS: Array - Time option labels for display
 * - onAddToSearch: Function - Handler for game click
 * - fetchGameDetails: Function - Handler to fetch game details for preview
 * - isInCollection: Function - Check if game is in collection
 * - openAddToCollectionModal: Function - Handler to add game to collection
 * - handleLetSPlay: Function - Handler for Let's Play button
 * - onNavigateBack: Function - Handler for back button
 * - onReset: Function - Handler for reset button
 * - setSelectedGames: Function - Clear selected games
 * - setRecommendations: Function - Clear recommendations
 * - onSearchSimilar: Function - Handler for Search Similar button
 * - onAddToMarketplace: Function - Handler for Add to Marketplace button
 * - onAddReminder: Function - Handler for Add Reminder button
 * - onCompareGames: Function - Handler for Compare Games button
 */

/* ── Match indicator helpers ── */
const getMatchIndicator = (score) => {
  if (score === null || score === undefined) return { symbol: 'N/A', color: 'var(--text-secondary, #888)', label: 'N/A' };
  const pct = score <= 1 ? score * 100 : score;
  if (pct >= 90) return { symbol: '✓', color: 'var(--success-color, #4caf50)', label: 'Excellent match' };
  if (pct >= 70) return { symbol: '◐', color: '#f5c518', label: 'Good match' };
  if (pct >= 50) return { symbol: '◑', color: '#ff9800', label: 'Fair match' };
  return { symbol: '✗', color: 'var(--error-color, #f44336)', label: 'Poor match' };
};

const getOverallScore = (game) => {
  if (game.overall_score !== undefined && game.overall_score !== null) return game.overall_score;
  if (game.final_score !== undefined && game.final_score !== null) return game.final_score;
  if (game.similarity_score !== undefined && game.similarity_score !== null) return game.similarity_score;
  if (game.scoreBreakdown) {
    const { playerScore, timeScore, energyScore, familiarityScore: fam } = game.scoreBreakdown;
    if (playerScore === -999) return 0;
    return 0.35 * (timeScore || 0) + 0.30 * (energyScore || 0) + 0.20 * (playerScore || 0) + 0.15 * (fam || 0);
  }
  return null;
};

const scorePct = (raw) => {
  if (raw === null || raw === undefined) return null;
  return raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
};

const scoreBadgeColor = (pct) => {
  if (pct === null) return 'var(--text-secondary, #888)';
  if (pct >= 80) return 'var(--success-color, #4caf50)';
  if (pct >= 60) return '#f5c518';
  if (pct >= 40) return '#ff9800';
  return 'var(--error-color, #f44336)';
};

/* ── Expanded Feedback Widget ── */
const FEEDBACK_REASONS = [
  { id: 'wrong_difficulty', label: 'Wrong difficulty' },
  { id: 'wrong_player_count', label: 'Wrong player count' },
  { id: 'wrong_length', label: 'Wrong length' },
  { id: 'not_my_taste', label: 'Not my taste' },
  { id: 'already_played', label: 'Already played' },
  { id: 'other', label: 'Other' },
];

const FeedbackWidget = ({ gameId, baseGameId, user }) => {
  const [phase, setPhase] = useState('idle'); // idle | expanded | done
  const [signal, setSignal] = useState(null);
  const [reasons, setReasons] = useState([]);
  const timerRef = useRef(null);

  const submit = async (sig, selectedReasons) => {
    if (!user?.id) return;
    try {
      await httpRequest(`${API_BASE}/recommendations/feedback`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: gameId,
          base_game_id: baseGameId ?? null,
          signal: sig,
          reasons: selectedReasons,
        }),
      });
      analyticsService.track('recommendation_feedback', { game_id: gameId, signal: sig, reasons: selectedReasons });
    } catch (err) {
      logger.warn('Failed to submit recommendation feedback:', err);
    }
  };

  const handleThumb = (sig) => {
    setSignal(sig);
    if (sig === 'thumbs_up') {
      submit(sig, []);
      setPhase('done');
    } else {
      setPhase('expanded');
    }
  };

  const toggleReason = (id) => {
    setReasons((prev) => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleSubmitReasons = () => {
    submit(signal, reasons);
    setPhase('done');
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (phase === 'done') {
    return (
      <div className="srp-feedback srp-feedback--done">
        Thanks for your feedback!
      </div>
    );
  }

  return (
    <div className="srp-feedback" data-testid="rec-feedback">
      {phase === 'idle' && (
        <div className="srp-feedback__row">
          <span className="srp-feedback__label">Was this helpful?</span>
          <button
            type="button"
            className="srp-feedback__thumb"
            onClick={() => handleThumb('thumbs_up')}
            title={user ? 'Good recommendation' : 'Sign in to rate'}
            disabled={!user}
            aria-label="Thumbs up"
            data-testid="feedback-up"
          ><ThumbsUp size={14} /> Yes</button>
          <button
            type="button"
            className="srp-feedback__thumb"
            onClick={() => handleThumb('thumbs_down')}
            title={user ? 'Not a good recommendation' : 'Sign in to rate'}
            disabled={!user}
            aria-label="Thumbs down"
            data-testid="feedback-down"
          ><ThumbsDown size={14} /> No</button>
        </div>
      )}
      {phase === 'expanded' && (
        <div className="srp-feedback__expanded">
          <p className="srp-feedback__question">Was this recommendation helpful?</p>
          <div className="srp-feedback__row">
            <button type="button" className="srp-feedback__thumb srp-feedback__thumb--active-up" disabled aria-label="Thumbs up"><ThumbsUp size={14} /> Yes</button>
            <button type="button" className="srp-feedback__thumb srp-feedback__thumb--active-down" disabled aria-label="Thumbs down"><ThumbsDown size={14} /> No</button>
          </div>
          <p className="srp-feedback__sub">What was wrong? <em>(optional)</em></p>
          <div className="srp-feedback__reasons">
            {FEEDBACK_REASONS.map(r => (
              <label key={r.id} className={`srp-feedback__reason${reasons.includes(r.id) ? ' srp-feedback__reason--checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={reasons.includes(r.id)}
                  onChange={() => toggleReason(r.id)}
                  aria-label={r.label}
                />
                {r.label}
              </label>
            ))}
          </div>
          <button type="button" className="srp-feedback__submit" onClick={handleSubmitReasons}>
            Submit
          </button>
        </div>
      )}
    </div>
  );
};

const SimplifyResultsPanel = ({
  recommendations,
  disqualifiedGames,
  preferences,
  toCamelCase,
  TIME_OPTIONS,
  onAddToSearch,
  fetchGameDetails,
  isInCollection,
  openAddToCollectionModal,
  handleLetSPlay,
  onNavigateBack,
  onReset,
  _setSelectedGames,
  _setRecommendations,
  onSearchSimilar,
  onAddToMarketplace,
  onAddReminder,
  onCompareGames,
  user,
  collection,
  onTogglePlayList,
  inPlayList = () => false,
  onExcludeGame,
}) => {
  const [shareStatus, setShareStatus] = useState('');
  const [showShareWarning, setShowShareWarning] = useState(() => isFirefox());
  const shareTimerRef = useRef(null);

  // Scroll to top of page when recommendations are shown
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Track funnel event: user saw recommendations
  useEffect(() => {
    if (recommendations && recommendations.length > 0) {
      analyticsService.track('recommendation_shown', { count: recommendations.length });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (shareTimerRef.current) clearTimeout(shareTimerRef.current); }, []);

  const handleShareResults = async () => {
    const prefLines = [
      preferences.players && `Players: ${preferences.players}`,
      preferences.feel && `Interaction: ${preferences.feel}`,
      preferences.energy && `Energy: ${preferences.energy}`,
      preferences.time && `Time: ${TIME_OPTIONS?.find(o => o.value === preferences.time)?.label || preferences.time}`,
    ].filter(Boolean).join(' · ');
    const gameLines = recommendations.slice(0, 5).map((g, i) => `${i + 1}. ${g.name || g.game_name}`).join('\n');
    const text = `My Jet Set Go picks:\n${prefLines}\n${gameLines}\n\nhttps://app.voyagerbug.com`;
    
    // Show Firefox warning if applicable
    if (isFirefox()) {
      setShowShareWarning(true);
    }
    
    try {
      if (navigator?.share) {
        await navigator.share({ title: 'My Jet Set Go picks', text });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShareStatus('Copied!');
    } catch {
      setShareStatus('Share not available');
    }
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
    shareTimerRef.current = setTimeout(() => setShareStatus(''), 2000);
    analyticsService.track('share_results', { count: recommendations.length });
  };

  const primaryResults = recommendations.slice(0, 2);
  const secondaryResults = recommendations.slice(2);

  // Bulk online availability for all recommended games
  const gameIds = useMemo(() => {
    const allGames = [...primaryResults, ...secondaryResults];
    return allGames
      .map(game => game.game_id || game.id)
      .filter(Boolean);
  }, [primaryResults, secondaryResults]);
  const { getGameAvailability } = useBulkOnlineAvailability(gameIds);

  const getGameOwnershipStatusForGame = useCallback(
    (gameId) => getGameOwnershipStatus(collection, gameId),
    [collection]
  );

  return (
    <div className="quick-decision-results">
      {/* Show user preferences with navigation buttons - anchored to top */}
      <div
        className="user-preferences"
        style={{
          position: "sticky",
          top: 0,
          backgroundColor: "var(--bg-secondary, #f5f5f5)",
          zIndex: 100,
          padding: "0 1rem 1rem 1rem",
          border: "1px solid var(--border-color, #ddd)",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginTop: 0,
        }}
      >
        {/* Row with Back (left), Title (center), Redo (right) */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0.5rem",
            position: "relative",
          }}
        >
          {/* Back button - top left */}
          <button
            className="back-button"
            onClick={onNavigateBack}
            style={{
              width: "40px",
              height: "40px",
              padding: 0,
              backgroundColor: "var(--success-color, #4caf50)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "1.2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArrowLeft size={18} />
          </button>

          {/* Title - centered */}
          <h4
            style={{
              margin: "0",
              color: "var(--text-primary, #333)",
              textAlign: "center",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            Your Preferences:
          </h4>

          {/* Redo button - top right */}
          <button
            className="back-button"
            onClick={onReset}
            style={{
              width: "40px",
              height: "40px",
              padding: 0,
              backgroundColor: "var(--success-color, #4caf50)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "1.2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Home size={16} />
          </button>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            fontSize: "0.9rem",
            justifyContent: "flex-start",
          }}
        >
          {preferences.players && (
            <span
              style={{
                padding: "0.25rem 0.5rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                whiteSpace: "nowrap",
                backgroundColor: "var(--bg-secondary, #f5f5f5)",
                border: "1px solid var(--primary-color, #2196f3)",
                borderRadius: "4px",
                color: "var(--primary-color, #1976d2)",
              }}
            >
              {GROUP_ICON} {preferences.players} Players
            </span>
          )}

          {preferences.feel && (
            <span
              style={{
                padding: "0.25rem 0.5rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                whiteSpace: "nowrap",
                backgroundColor: "var(--info-light, #e1f5fe)",
                border: "1px solid var(--primary-color, #03a9f4)",
                borderRadius: "4px",
                color: "var(--primary-color, #1976d2)",
              }}
            >
              <Target size={14} /> {getFeelDisplayLabel(preferences.feel)} Interaction
            </span>
          )}

          {preferences.energy && (
            <span
              style={{
                padding: "0.25rem 0.5rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                whiteSpace: "nowrap",
                backgroundColor: "var(--warning-light, #fff3e0)",
                border: "1px solid var(--warning-color, #ff9800)",
                borderRadius: "4px",
                color: "var(--warning-color-dark, #e65100)",
              }}
            >
              <Zap size={14} /> {ENERGY_OPTIONS.find((opt) => opt.value === preferences.energy)?.label || toCamelCase(preferences.energy)} Energy Level
            </span>
          )}

          {preferences.time && (
            <span
              style={{
                padding: "0.25rem 0.5rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                whiteSpace: "nowrap",
                backgroundColor: "var(--success-light, #e8f5e9)",
                border: "1px solid var(--success-color, #4caf50)",
                borderRadius: "4px",
                color: "var(--success-color, #4caf50)",
              }}
            >
              {TIMER_ICON}{" "}
              {TIME_OPTIONS.find((opt) => opt.value === preferences.time)
                ?.label || preferences.time}{" "}
              Playtime
            </span>
          )}

          {preferences.familiarity && preferences.familiarity !== 'any' && (
            <span
              style={{
                padding: "0.25rem 0.5rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                whiteSpace: "nowrap",
                backgroundColor: "var(--bg-secondary, #f3e5f5)",
                border: "1px solid var(--primary-color, #9c27b0)",
                borderRadius: "4px",
                color: "var(--primary-color-dark, #6a1b9a)",
              }}
            >
              {HEART_ICON} {FAMILIARITY_OPTIONS.find((opt) => opt.value === preferences.familiarity)?.label || toCamelCase(preferences.familiarity)}
            </span>
          )}
        </div>
      </div>

      {/* Progress indicator */}
      <div className="spp-progress spp-progress--step3" aria-label="Step 3 of 3: Results">
        <div className="spp-progress-bar">
          <div className="spp-progress-bar__fill" />
        </div>
        <span className="spp-progress-label">Step 3 of 3: Results</span>
      </div>

      {/* Share results */}
      {recommendations.length > 0 && (
        <div className="srp-share-row">
          {showShareWarning && (
            <ShareWarningBanner 
              message="Share functionality may be limited in Firefox. Contents copied to clipboard, ready to share."
              className="simplify-share-warning"
            />
          )}
          <button
            type="button"
            className="srp-share-btn"
            onClick={handleShareResults}
            aria-label="Share these results"
          >
            <Share2 size={15} />
            {shareStatus || 'Share these results'}
          </button>
        </div>
      )}

      {recommendations.length > 0 ? (
        <>
          {/* Primary results (#1 and #2) */}
          <div
            className="primary-recommendations"
            style={{ marginBottom: "2rem" }}
          >
            {primaryResults.map((game, index) => (
              <div
                key={game.id}
                className="primary-recommendation"
                style={{ marginBottom: "1.5rem" }}
              >
                {/* Rank + score badge row */}
                <div className="srp-card-header">
                  <span
                    className="recommendation-rank"
                    style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--primary-color, #1976d2)" }}
                  >
                    #{index + 1}
                  </span>
                  {(() => {
                    const raw = getOverallScore(game);
                    const pct = scorePct(raw);
                    const { symbol, label } = getMatchIndicator(raw);
                    return pct !== null ? (
                      <span
                        className="srp-score-badge"
                        style={{ backgroundColor: scoreBadgeColor(pct), color: '#fff' }}
                        title={label}
                        aria-label={`${label}: ${pct}%`}
                      >
                        <span className="srp-score-badge__symbol" aria-hidden="true">{symbol}</span>
                        {pct}% match
                      </span>
                    ) : null;
                  })()}
                </div>

                <ExplanationPills scoreBreakdown={game.scoreBreakdown} familiarityPref={preferences.familiarity} />

                <GameTile
                  game={game}
                  tileIdx={index}
                  chipsToShow={999}
                  onGameClick={onAddToSearch}
                  onRequireFeature={undefined}
                  messageIndex={undefined}
                  variant={undefined}
                  differences={undefined}
                  expandedGames={undefined}
                  toggleExpand={undefined}
                  querySpec={undefined}
                  onAddToSearch={onAddToSearch}
                  setPreviewGame={fetchGameDetails}
                  showAddToCollection={true}
                  isInCollection={isInCollection(game.id || game.game_id)}
                  onAddToCollection={() => { openAddToCollectionModal(game); }}
                  hideDescription={true}
                  onLetSPlay={handleLetSPlay}
                  hideMarketplaceButton={true}
                  hideSimilarity={true}
                  hideFeedback={true}
                  getGameAvailability={getGameAvailability}
                  user={user}
                  onSearchSimilar={onSearchSimilar}
                  onAddToMarketplace={onAddToMarketplace}
                  onAddReminder={onAddReminder}
                  onCompareGames={onCompareGames}
                  getGameOwnershipStatus={getGameOwnershipStatusForGame}
                  onTogglePlayList={onTogglePlayList}
                  inPlayList={inPlayList(game.id || game.game_id)}
                  onExcludeGame={onExcludeGame}
                />

                <FeedbackWidget
                  gameId={game.game_id || game.id}
                  baseGameId={game.base_game_id ?? null}
                  user={user}
                />
              </div>
            ))}
          </div>

          {/* Secondary results (#3 and below) */}
          {secondaryResults.length > 0 && (
            <div className="secondary-recommendations" style={{ padding: "0 1rem" }}>
              <h4
                style={{
                  fontSize: "1.2rem",
                  color: "var(--text-secondary, #666)",
                  marginBottom: "1rem",
                  fontWeight: "normal",
                }}
              >
                Other Recommendations:
              </h4>
              <div className="secondary-results-grid">
                {secondaryResults.map((game, index) => (
                  <div key={game.id} className="secondary-recommendation">
                    {/* Rank + score badge row */}
                    <div className="srp-card-header">
                      <span
                        className="recommendation-rank"
                        style={{ fontSize: "1rem", fontWeight: "bold", color: "var(--text-secondary, #666)" }}
                      >
                        #{index + 3}
                      </span>
                      {(() => {
                        const raw = getOverallScore(game);
                        const pct = scorePct(raw);
                        const { symbol, label } = getMatchIndicator(raw);
                        return pct !== null ? (
                          <span
                            className="srp-score-badge srp-score-badge--sm"
                            style={{ backgroundColor: scoreBadgeColor(pct), color: '#fff' }}
                            title={label}
                            aria-label={`${label}: ${pct}%`}
                          >
                            <span className="srp-score-badge__symbol" aria-hidden="true">{symbol}</span>
                            {pct}%
                          </span>
                        ) : null;
                      })()}
                    </div>

                    <ExplanationPills scoreBreakdown={game.scoreBreakdown} familiarityPref={preferences.familiarity} />

                    <GameTile
                      game={game}
                      tileIdx={index + 2}
                      chipsToShow={999}
                      onGameClick={onAddToSearch}
                      onRequireFeature={undefined}
                      messageIndex={undefined}
                      variant={undefined}
                      differences={undefined}
                      expandedGames={undefined}
                      toggleExpand={undefined}
                      querySpec={undefined}
                      onAddToSearch={onAddToSearch}
                      setPreviewGame={fetchGameDetails}
                      showAddToCollection={true}
                      isInCollection={isInCollection(game.id || game.game_id)}
                      onAddToCollection={() => { openAddToCollectionModal(game); }}
                      hideDescription={true}
                      onLetSPlay={handleLetSPlay}
                      hideMarketplaceButton={true}
                      hideSimilarity={true}
                      hideFeedback={true}
                      getGameAvailability={getGameAvailability}
                      user={user}
                      onSearchSimilar={onSearchSimilar}
                      onAddToMarketplace={onAddToMarketplace}
                      onAddReminder={onAddReminder}
                      onCompareGames={onCompareGames}
                      getGameOwnershipStatus={getGameOwnershipStatusForGame}
                      onTogglePlayList={onTogglePlayList}
                      inPlayList={inPlayList(game.id || game.game_id)}
                      onExcludeGame={onExcludeGame}
                    />

                    <FeedbackWidget
                      gameId={game.game_id || game.id}
                      baseGameId={game.base_game_id ?? null}
                      user={user}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disqualified games section */}
          {disqualifiedGames.length > 0 && (
            <div className="disqualified-games" style={{
              marginTop: "2rem",
              border: "2px solid var(--error-color, #ffcdd2)",
              borderRadius: "8px",
              padding: "1rem",
              backgroundColor: "var(--bg-secondary, #ffebee)",
              marginLeft: "1rem",
              marginRight: "1rem"
            }}>
              <h4
                style={{
                  fontSize: "1.2rem",
                  color: "var(--text-secondary, #666)",
                  marginBottom: "1rem",
                  fontWeight: "normal",
                }}
              >
                Disqualified Games:
              </h4>
              <div className="disqualified-results-grid">
                {disqualifiedGames.map((game, index) => (
                  <div key={game.id} className="disqualified-game">
                    {/* Explanation pills */}
                    <ExplanationPills
                      scoreBreakdown={game.scoreBreakdown}
                      familiarityPref={preferences.familiarity}
                      style={{ justifyContent: "center" }}
                    />

                    {/* Banner overlay anchored to the tile only */}
                    <div style={{ position: "relative", width: "100%" }}>
                      <div className="disqualified-banner">Disqualified</div>

                    <GameTile
                      game={game}
                      tileIdx={index}
                      chipsToShow={999}
                      onGameClick={onAddToSearch}
                      onRequireFeature={undefined}
                      messageIndex={undefined}
                      variant={undefined}
                      differences={undefined}
                      expandedGames={undefined}
                      toggleExpand={undefined}
                      querySpec={undefined}
                      onAddToSearch={onAddToSearch}
                      setPreviewGame={fetchGameDetails}
                      showAddToCollection={true}
                      isInCollection={isInCollection(game.id || game.game_id)}
                      onAddToCollection={() => {
                        logger.debug("[SimplifyResultsPanel] Adding disqualified game:", game);
                        openAddToCollectionModal(game);
                      }}
                      hideDescription={true}
                      onLetSPlay={handleLetSPlay}
                      hideSimilarity={true}
                      getGameAvailability={getGameAvailability}
                      user={user}
                      onSearchSimilar={onSearchSimilar}
                      onAddToMarketplace={onAddToMarketplace}
                      onAddReminder={onAddReminder}
                      onCompareGames={onCompareGames}
                      getGameOwnershipStatus={getGameOwnershipStatusForGame}
                      onTogglePlayList={onTogglePlayList}
                      inPlayList={inPlayList(game.id || game.game_id)}
                      onExcludeGame={onExcludeGame}
                    />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="no-recommendations">
          <p>
            No games match your criteria. Try adjusting your preferences or
            selecting different games.
          </p>
        </div>
      )}
    </div>
  );
};

export default SimplifyResultsPanel;
